import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowPathIcon, Cog6ToothIcon } from '@heroicons/react/24/outline';

import Layout from '../../components/layout/Layout';
import RAGFlowConversationView from '../../components/data-analyst/RAGFlowConversationView';
import { Alert, Button, Page, PageBody, PageHeader, Spinner } from '../../components/ui';
import { getApiErrorMessage, getBVInsightsWorkspace } from './api';
import BVPageTitle from './components/BVPageTitle';
import { BVInsightsWorkspace } from './types';

export default function BVReviewsInsightsPage() {
  const navigate = useNavigate();
  const { convId } = useParams<{ convId?: string }>();

  const [workspace, setWorkspace] = useState<BVInsightsWorkspace | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const loadWorkspace = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const nextWorkspace = await getBVInsightsWorkspace();
        if (!isMounted) {
          return;
        }
        setWorkspace(nextWorkspace);
      } catch (nextError) {
        if (!isMounted) {
          return;
        }
        setWorkspace(null);
        setError(getApiErrorMessage(nextError, 'Failed to load the Bazaarvoice Insights workspace'));
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadWorkspace();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleConversationCreated = useCallback(
    (uuid: string) => {
      navigate(`/bv-reviews/insights/${uuid}`, { replace: true });
    },
    [navigate],
  );

  const handleBack = useCallback(() => {
    navigate('/bv-reviews/studio');
  }, [navigate]);

  if (isLoading) {
    return (
      <Layout>
        <div className="flex h-full items-center justify-center">
          <Spinner size="lg" />
        </div>
      </Layout>
    );
  }

  if (error || !workspace) {
    return (
      <Layout>
        <Page maxWidth="xl">
          <PageHeader
            title={<BVPageTitle title="Review Intelligence Insights Chat" />}
            description="Open a workspace-backed chat over the Bazaarvoice review corpus."
            actions={
              <Button asChild variant="outline">
                <Link to="/bv-reviews/settings">
                  <Cog6ToothIcon className="mr-2 h-4 w-4" />
                  Open Settings
                </Link>
              </Button>
            }
          />
          <PageBody>
            <Alert variant="error" title="Insights workspace unavailable">
              {error ?? 'The Bazaarvoice Insights workspace could not be loaded for this tenant.'}
            </Alert>
          </PageBody>
        </Page>
      </Layout>
    );
  }

  if (workspace.status !== 'ready') {
    return (
      <Layout>
        <Page maxWidth="xl">
          <PageHeader
            title={<BVPageTitle title="Review Intelligence Insights Chat" />}
            description="The BV review workspace must finish bootstrapping before chat can answer grounded questions."
            actions={
              <div className="flex gap-3">
                <Button asChild variant="outline">
                  <Link to="/bv-reviews/settings">
                    <Cog6ToothIcon className="mr-2 h-4 w-4" />
                    Open Settings
                  </Link>
                </Button>
                <Button onClick={() => window.location.reload()}>
                  <ArrowPathIcon className="mr-2 h-4 w-4" />
                  Refresh status
                </Button>
              </div>
            }
          />
          <PageBody>
            <Alert variant="info" title="Workspace still preparing">
              The seeded BV workspace is currently `{workspace.status}`. Return to Settings to run or monitor
              ingestion, then refresh this page once the workspace is ready.
            </Alert>
          </PageBody>
        </Page>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="flex h-full min-h-0 w-full min-w-0 flex-col overflow-hidden">
        <RAGFlowConversationView
          domainId={workspace.id}
          domainName={workspace.display_name || workspace.name}
          conversationId={convId}
          disableSourceInteractions
          sourceMetadataMode="bv-reviews"
          onBack={handleBack}
          onConversationCreated={handleConversationCreated}
        />
      </div>
    </Layout>
  );
}
